import { endOfDay, isWithinInterval, startOfDay } from 'date-fns';
import { getAppointmentOperationalStatus } from '@/lib/work-items/appointmentRescheduleState';

import type { WorkItem } from '../UniversalWorkItems';

export const SCHEDULE_HIDDEN_STATUSES = ['completed', 'done', 'closed', 'cancelled', 'archived', 'resolved', 'ai review', 'on hold'] as const;
const SCHEDULE_VISIBLE_SCHEDULED_APPOINTMENT_STATUSES = ['completed', 'done', 'closed', 'resolved'] as const;

export const SCHEDULE_SIDEBAR_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'new', label: 'New' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'scheduled', label: 'Scheduled' },
] as const;

export const SCHEDULE_SIDEBAR_PRIORITY_OPTIONS = [
  { value: 'all', label: 'All Priorities' },
  { value: 'critical', label: 'Critical' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

export const SCHEDULE_SIDEBAR_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest → Oldest' },
  { value: 'oldest', label: 'Oldest → Newest' },
] as const;

export type ScheduleSidebarSortOrder = 'newest' | 'oldest';

export interface ScheduleSidebarContactOption {
  value: string;
  label: string;
}

export interface ScheduleVisibilityOptions {
  preserveScheduledAppointments?: boolean;
}

function matchesScheduleSearch(item: WorkItem, normalizedQuery: string): boolean {
  return Boolean(
    item.title?.toLowerCase().includes(normalizedQuery) ||
    item.item_number?.toLowerCase().includes(normalizedQuery) ||
    item.contact_name?.toLowerCase().includes(normalizedQuery)
  );
}

function matchesStatusFilter(item: WorkItem, statusFilter: string): boolean {
  return statusFilter === 'all' || item.status === statusFilter;
}

function matchesPriorityFilter(item: WorkItem, priorityFilter: string): boolean {
  return priorityFilter === 'all' || item.priority === priorityFilter;
}

function getNormalizedContactName(item: WorkItem): string {
  return item.contact_name?.trim().toLowerCase() || '';
}

function matchesContactFilter(item: WorkItem, contactFilter: string): boolean {
  return contactFilter === 'all' || getNormalizedContactName(item) === contactFilter;
}

function sortScheduleSidebarItems(items: WorkItem[], sortOrder: ScheduleSidebarSortOrder): WorkItem[] {
  const sortedItems = [...items];

  sortedItems.sort((left, right) => {
    const leftValue = Date.parse(left.created_at || '') || 0;
    const rightValue = Date.parse(right.created_at || '') || 0;

    return sortOrder === 'oldest'
      ? leftValue - rightValue
      : rightValue - leftValue;
  });

  return sortedItems;
}

function applyScheduleSidebarFilters(
  items: WorkItem[],
  {
    searchQuery = '',
    statusFilter = 'all',
    priorityFilter = 'all',
    contactFilter = 'all',
    sortOrder = 'newest',
  }: {
    searchQuery?: string;
    statusFilter?: string;
    priorityFilter?: string;
    contactFilter?: string;
    sortOrder?: ScheduleSidebarSortOrder;
  }
): WorkItem[] {
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredItems = items.filter((item) => {
    if (normalizedQuery && !matchesScheduleSearch(item, normalizedQuery)) return false;
    if (!matchesStatusFilter(item, statusFilter)) return false;
    if (!matchesPriorityFilter(item, priorityFilter)) return false;
    if (!matchesContactFilter(item, contactFilter)) return false;
    return true;
  });

  return sortScheduleSidebarItems(filteredItems, sortOrder);
}

function getJobsScheduledForSelectedDate(items: WorkItem[], selectedDate: Date): Set<string> {
  const selectedDayStart = startOfDay(selectedDate);
  const selectedDayEnd = endOfDay(selectedDate);
  const scheduledJobIds = new Set<string>();

  items.forEach((item) => {
    if (item.item_type !== 'appointment' || !item.parent_id || !item.scheduled_start) {
      return;
    }

    const scheduledStart = new Date(item.scheduled_start);
    const scheduledEnd = item.scheduled_end ? new Date(item.scheduled_end) : scheduledStart;
    const overlapsSelectedDay = isWithinInterval(scheduledStart, { start: selectedDayStart, end: selectedDayEnd })
      || isWithinInterval(scheduledEnd, { start: selectedDayStart, end: selectedDayEnd })
      || (scheduledStart <= selectedDayStart && scheduledEnd >= selectedDayEnd);

    if (overlapsSelectedDay) {
      scheduledJobIds.add(item.parent_id);
    }
  });

  return scheduledJobIds;
}

export function getScheduleSidebarContactOptions(items: WorkItem[]): ScheduleSidebarContactOption[] {
  const contacts = new Map<string, string>();

  items.forEach((item) => {
    const label = item.contact_name?.trim();
    if (!label) return;

    const value = label.toLowerCase();
    if (!contacts.has(value)) {
      contacts.set(value, label);
    }
  });

  return Array.from(contacts.entries())
    .sort((left, right) => left[1].localeCompare(right[1]))
    .map(([value, label]) => ({ value, label }));
}

export function isScheduleHiddenItem(item: WorkItem, options?: ScheduleVisibilityOptions): boolean {
  const operationalStatus = item.item_type === 'appointment'
    ? getAppointmentOperationalStatus(item as Record<string, any>)
    : item.status;

  if (
    options?.preserveScheduledAppointments
    && item.item_type === 'appointment'
    && item.scheduled_start
    && item.scheduled_end
    && SCHEDULE_VISIBLE_SCHEDULED_APPOINTMENT_STATUSES.includes(
      operationalStatus as typeof SCHEDULE_VISIBLE_SCHEDULED_APPOINTMENT_STATUSES[number]
    )
  ) {
    return false;
  }

  return SCHEDULE_HIDDEN_STATUSES.includes(operationalStatus as typeof SCHEDULE_HIDDEN_STATUSES[number]);
}

export function filterScheduleVisibleItems(items: WorkItem[], options?: ScheduleVisibilityOptions): WorkItem[] {
  return items.filter((item) => !isScheduleHiddenItem(item, options));
}

export function getGenericUnscheduledScheduleItems({
  items,
  searchQuery = '',
  statusFilter = 'all',
  priorityFilter = 'all',
  contactFilter = 'all',
  sortOrder = 'newest',
}: {
  items: WorkItem[];
  searchQuery?: string;
  statusFilter?: string;
  priorityFilter?: string;
  contactFilter?: string;
  sortOrder?: ScheduleSidebarSortOrder;
}): WorkItem[] {
  const unscheduledItems = filterScheduleVisibleItems(items).filter(item => {
    if (item.scheduled_start && item.scheduled_end) return false;
    return true;
  });

  return applyScheduleSidebarFilters(unscheduledItems, {
    searchQuery,
    statusFilter,
    priorityFilter,
    contactFilter,
    sortOrder,
  });
}

export function getUnscheduledJobsForSelectedDate({
  items,
  selectedDate,
  excludeJobsWithAppointmentsOnSelectedDate = false,
  searchQuery = '',
  statusFilter = 'all',
  priorityFilter = 'all',
  contactFilter = 'all',
  sortOrder = 'newest',
}: {
  items: WorkItem[];
  selectedDate: Date;
  excludeJobsWithAppointmentsOnSelectedDate?: boolean;
  searchQuery?: string;
  statusFilter?: string;
  priorityFilter?: string;
  contactFilter?: string;
  sortOrder?: ScheduleSidebarSortOrder;
}): WorkItem[] {
  const jobsScheduledForSelectedDate = excludeJobsWithAppointmentsOnSelectedDate
    ? getJobsScheduledForSelectedDate(items, selectedDate)
    : null;

  const unscheduledJobs = filterScheduleVisibleItems(items).filter(item => {
    if (item.item_type !== 'job') return false;
    if (jobsScheduledForSelectedDate?.has(item.id)) return false;
    return true;
  });

  return applyScheduleSidebarFilters(unscheduledJobs, {
    searchQuery,
    statusFilter,
    priorityFilter,
    contactFilter,
    sortOrder,
  });
}
